import type { PersonPaalaegDTO } from "@/api/person/paalaeg";
import { TableBody, TableHead, TableRoot } from "@/components/Table/TableBasic";
import { formatLessons } from "@/util/number";

import {
  mapIndberetningStatusBadgeVariant,
  mapIndberetningStatusLabel,
} from "./paalaegUtils";
import { StatusBadge } from "./StatusBadge";
import { formatDate } from "./utils/formatters";
import { formaterFrist, formaterIndfrielsesperiode } from "./injunctionPeriode";

interface PaalaegTableProps {
  paalaegList: PersonPaalaegDTO[];
  isLoading: boolean;
  error: Error | null;
  onSelectPaalaeg: (paalaegId: string) => void;
}

export const PaalaegTable = ({
  paalaegList,
  isLoading,
  error,
  onSelectPaalaeg,
}: PaalaegTableProps) => (
  <TableRoot>
    <TableHead>
      <th>Infrielsesperiode</th>
      <th>Påkrævede lektioner</th>
      <th>Lektioner gennemført</th>
      <th>Overførte lektioner</th>
      <th>Status</th>
      <th>Frist</th>
      <th className="text-end">Handling</th>
    </TableHead>
    <TableBody>
      {isLoading && (
        <tr>
          <td colSpan={7} className="text-muted">
            Henter pålæg ...
          </td>
        </tr>
      )}
      {error && (
        <tr>
          <td colSpan={7} className="text-danger">
            Kunne ikke hente pålæg.
          </td>
        </tr>
      )}
      {!isLoading &&
        !error &&
        paalaegList.map((paalaeg) => {
          const title =
            paalaeg.decisionDocumentFileName ??
            `${formatLessons(paalaeg.paakraevedeLektioner)} lektioner`;
          const indberetningStatus =
            paalaeg.indberetning?.efteruddannelseIndberetningStatus;
          const statusLabel = mapIndberetningStatusLabel(indberetningStatus);

          return (
            <tr
              key={paalaeg.id}
              aria-label={`Vis pålæg ${title}`}
              className="cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onSelectPaalaeg(paalaeg.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectPaalaeg(paalaeg.id);
                }
              }}
            >
              <td>
                {formaterIndfrielsesperiode(
                  paalaeg.periodeFra,
                  paalaeg.periodeTil,
                  formatDate,
                )}
              </td>
              <td>{paalaeg.paakraevedeLektioner}</td>
              <td>{formatLessons(paalaeg.lektionerGennemfoert)}</td>
              <td>{formatLessons(paalaeg.lektionerOverfoert)}</td>
              <td>
                {indberetningStatus ? (
                  <StatusBadge
                    status={statusLabel}
                    variant={mapIndberetningStatusBadgeVariant(
                      indberetningStatus,
                    )}
                  />
                ) : (
                  "-"
                )}
              </td>
              <td>
                {formaterFrist(
                  paalaeg.indberetning?.indberetningFrist,
                  formatDate,
                )}
              </td>
              <td className="text-end">
                <button
                  className="btn btn--action btn--sm"
                  id={`efteruddannelse-paalaeg-vis-${paalaeg.id}`}
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSelectPaalaeg(paalaeg.id);
                  }}
                >
                  Vis
                </button>
              </td>
            </tr>
          );
        })}
      {!isLoading && !error && !paalaegList.length && (
        <tr>
          <td colSpan={7} className="text-muted">
            Ingen pålæg
          </td>
        </tr>
      )}
    </TableBody>
  </TableRoot>
);
